// ─── Render protocol types ─────────────────────────────────────────────────────
// These types are shared between the app components and the v1-chat edge function.
// The AI uses render_ui tool calls to provide data for RenderPart responses.
// It must never generate renderer code; the app dispatches each part to the
// appropriate built-in renderer component.
//
// ARCHITECTURE: These interfaces are the stable data contract between the AI and
// the UI. Renderer files (ChartRenderer, CalendarRenderer, etc.) can swap their
// underlying library without touching these types.

export type RenderComponent =
  | 'calendar'
  | 'booking_list'
  | 'table'
  | 'chart'
  | 'booking_card'
  | 'package_summary'
  | 'timeline'
  | 'customer_confirm'

export interface TextPart {
  kind: 'text'
  content: string
}

export interface RenderPart {
  kind: 'render'
  component: RenderComponent
  props: unknown
  caption?: string
}

export type ResponsePart = TextPart | RenderPart

// ─── Calendar ─────────────────────────────────────────────────────────────────

// Lifecycle: booked → completed | cancelled | did_not_show. 'rescheduled' is
// terminal on a superseded row (the replacement links back via previous_booking_id),
// not a forward step. A past booking still on 'booked' means nobody closed it off.
export type BookingStatus = 'booked' | 'cancelled' | 'completed' | 'did_not_show' | 'rescheduled'
export type PaymentStatus = 'paid' | 'partial' | 'unpaid'

// ── Attendance ────────────────────────────────────────────────────────────────
//
// Booked → Checked In → Checked Out, and it rides on TWO TIMESTAMPS rather than
// two new status values (2026-08-17). The status vocabulary is duplicated across
// five places — the CHECK constraint in both DDL copies, try_create_booking, the
// eight report_* functions, this file's status colour/tone maps, and
// migration/transform.mjs — and a value one report_* function forgets silently
// drops revenue from the rollups. Timestamps add no vocabulary anywhere.
//
// Both fields come from a real producer (v1-bookings selects them, lib/calendar.ts
// maps them). That matters: there used to be a `checked_in?: boolean` here that
// NOTHING ever set, so the tick it drove could never appear. A render field with
// no producer is a feature that silently does not exist.

export type AttendanceState = 'not_arrived' | 'in_session' | 'checked_out'

/**
 * The visible attendance state, derived so the calendar and the booking record
 * cannot disagree.
 *
 * Read in this order, and the order is the point:
 *
 * 1. **`status === 'completed'` means finished**, timestamp or not. Every booking
 *    completed before 2026-08-17 has no stamps at all, and so does every one
 *    completed through chat by an operator who never checks anybody in. Testing
 *    the stamps alone would show the whole existing history as "not arrived" and
 *    invite an operator to check in a session that happened last March.
 * 2. `checked_out_at` — finished, even with no arrival recorded.
 * 3. `checked_in_at` — in the building.
 */
export function attendanceState(
  event: Partial<Pick<CalendarEvent, 'status' | 'checked_in_at' | 'checked_out_at'>>,
): AttendanceState {
  if (event.status === 'completed' || event.checked_out_at) return 'checked_out'
  if (event.checked_in_at) return 'in_session'
  return 'not_arrived'
}

/**
 * In the building right now: arrived and not yet finished.
 *
 * Narrower than it used to be — it meant `status === 'completed'` until
 * 2026-08-17, when "arrived" and "finished" became different things. Callers
 * wanting "has this session happened at all" want `attendanceState() !==
 * 'not_arrived'`.
 */
export function isCheckedIn(
  event: Partial<Pick<CalendarEvent, 'status' | 'checked_in_at' | 'checked_out_at'>>,
): boolean {
  return attendanceState(event) === 'in_session'
}

/** Arrived at some point — in session or finished. What a tile's tick means. */
export function hasArrived(
  event: Partial<Pick<CalendarEvent, 'status' | 'checked_in_at' | 'checked_out_at'>>,
): boolean {
  return attendanceState(event) !== 'not_arrived'
}

export interface CalendarEvent {
  id: string
  start: string          // ISO datetime e.g. "2026-05-18T09:00:00"
  end: string            // ISO datetime
  service_name: string
  service_order?: number // service display_order
  customer_name?: string
  customer_id?: string   // lets a tile open the customer record
  status: BookingStatus
  payment_status?: PaymentStatus
  // Settled by a package credit rather than cash. Such a booking is `paid` even
  // though amount_paid is 0 — the money arrived when the package was sold.
  paid_with_package?: boolean
  /**
   * Tenant-local naive datetimes. Absent = never recorded, which for
   * `checked_in_at` reads as "not arrived" — correct for a future booking, and
   * correct for the whole existing history, which has no arrival times because
   * they were never captured. Read them through `attendanceState`, never directly.
   */
  checked_in_at?: string | null
  checked_out_at?: string | null
  /**
   * The tile line's enrichment (`🟢 Sara Walling · 1/6 🎂`) — written ONLY by
   * `enrichEvents` in lib/daySheet.ts from the /schedule day-sheet fetch. These
   * are deliberately NOT in v1-chat's render_ui schema: the model never writes
   * them (it would be retyping facts, the booking_list failure), so the AI's
   * chat calendars simply render tiles without a dot. Absence hides the marks;
   * it never hides the tile.
   */
  readiness?: 'ready' | 'attention' | 'blocked'
  readiness_label?: string
  credit_label?: string
  birthday_label?: string
  /** Intake against the active default form — enrichment, same producer. */
  intake_status?: 'complete' | 'pending' | 'not_sent'
  /** A completed intake flagged medical clearance nobody has granted yet. */
  clearance_pending?: boolean
  resource_name?: string
  resource_order?: number // machine display_order — drives day-view column ordering
  resource_id?: string
  resource_ids?: string[]
  color?: string
}

export interface CalendarResource {
  id: string
  name: string
  type?: string
  display_order?: number // sort position for day-view machine columns (ascending)
  /**
   * Absent means active. A RETIRED machine can still hold a column — a past
   * booking in view still names the machine it ran on — but it must never be
   * offered as bookable time: it produces no slots and is never auto-selected,
   * so a click-to-book band on it could only fail. See SlotBandLayer.
   */
  is_active?: boolean
}

// ── The two non-booking things that appear on a calendar ──────────────────────
//
// They look similar and behave oppositely, which is why they are separate types
// rather than one with a flag:
//
//   CalendarBlock — CLOSED time. Nothing can be booked in it, availability
//                   excludes it for every service, and the grid must not offer a
//                   slot band over it.
//   CalendarNote  — a MEMO. The time stays bookable and availability never sees
//                   it; it is a reminder for whoever is working that day.
//
// Both are OPTIONAL on CalendarProps and today only the /schedule screen supplies
// them — the AI's chat calendars answer "how does Friday look" from bookings
// alone. An absent array means "not known", never "none exist".

export interface CalendarBlock {
  id: string
  /** Tenant-local naive datetime. */
  start: string
  end: string
  /** null = the whole business is closed, so this applies to every column. */
  resource_id: string | null
  reason?: string | null
}

export interface CalendarNote {
  id: string
  /** Tenant-local naive datetime — where on the grid the memo sits. */
  at: string
  note: string
  /** null = the memo is about the day rather than one machine. */
  resource_id: string | null
}

export interface CalendarProps {
  view?: 'day' | 'week' | 'resources'  // defaults to 'day'
  date: string           // anchor date ISO YYYY-MM-DD
  events: CalendarEvent[]
  resources?: CalendarResource[]
  business_hours?: { open: string; close: string }  // e.g. "08:00", "20:00"
  slot_minutes?: number
  /** Closures overlapping the drawn window. Absent = not known, not "none". */
  blocks?: CalendarBlock[]
  /** Memos in the drawn window. These never affect what is bookable. */
  notes?: CalendarNote[]
}

// ─── Booking list ─────────────────────────────────────────────────────────────
// The counterpart to the calendar, and the two are NOT interchangeable.
//
// A calendar answers "what does this day look like": one anchor date, a bounded
// grid, machine columns, gaps that mean free time. It only works when the
// question really is a day (or a week) of the whole business.
//
// A booking list answers "which appointments match this": one customer's
// history, an open-ended range ("all future bookings"), or a filtered set
// (unpaid, no-shows). Those have no single anchor date, so a calendar would
// either drop every row outside its anchor day or spread three appointments
// across an hour grid that is 95% empty. That is the bug this component exists
// to fix — see the render rules in the v1-chat operator prompt.

export interface BookingListItem {
  id: string
  start: string          // tenant-local naive 'YYYY-MM-DDTHH:mm'
  end?: string
  service_name: string
  customer_name?: string
  status: BookingStatus
  payment_status?: PaymentStatus
  // Settled by a package credit: `paid` with amount_paid still 0, same rule as
  // CalendarEvent.
  paid_with_package?: boolean
  resource_name?: string
  price?: number
  balance_due?: number
}

export interface BookingListProps {
  title?: string
  bookings: BookingListItem[]
  /** Total matching rows, when more exist than the page being shown. */
  total?: number
  currency?: string
}

// ─── Chart ────────────────────────────────────────────────────────────────────

export interface ChartDataset {
  label: string
  data: number[]
  color?: string  // hex color, e.g. "#3b82f6"
}

export interface ChartProps {
  type: 'bar' | 'line' | 'area' | 'pie'
  title?: string
  labels: string[]        // x-axis category labels (or pie slice labels)
  datasets: ChartDataset[]
  unit?: string           // y-axis unit prefix: '$' for currency, '%' for percent
  yAxisLabel?: string
}

// ─── Other component prop types ───────────────────────────────────────────────

export interface TableProps {
  columns: string[]
  rows: (string | number | null)[][]
  title?: string
}

export interface BookingCardProps {
  booking: {
    id: string
    start: string
    end: string
    service_name: string
    customer_name?: string
    status: string
    price: number
    currency: string
    balance_due?: number
  }
}

export interface PackageSummaryProps {
  package: {
    id: string
    name: string
    sessions_total: number
    price: number
    currency: string
  }
  customer_package: {
    id: string
    sessions_used: number
    expires_at: string | null
    purchased_at: string
  }
}

export interface TimelineProps {
  events: Array<{
    time: string
    title: string
    description?: string
    color?: string
  }>
}

// ─── Customer confirmation ────────────────────────────────────────────────────
// Rendered when a name the operator used matched more than one customer.
// The AI proposes `customer` (its top-ranked match) and lists the runners-up;
// the operator answers with buttons. The reply is sent as a normal chat
// message and MUST carry the chosen customer id in its text — chat history is
// text-only, so an id living only in a tool result gets invented later.

export interface CustomerConfirmCandidate {
  id: string
  name: string
  phone: string
  // Why this candidate ranks where it does, e.g. "appointment today 2:00 pm".
  context?: string
}

export interface CustomerConfirmProps {
  question?: string
  customer: CustomerConfirmCandidate
  alternatives?: CustomerConfirmCandidate[]
}
